import { beginLoading, endLoading } from "@app/AppLayout/LoadingContext";
import { useNotification } from '@app/AppLayout/NotificationContext';
import { DataLoadErrorRow } from '@app/components/DataLoadError';
import { PageActionsMenu } from '@app/components/PageActionsMenu';
import { useEntity } from "@app/hooks/useEntity";
import { useTablePreferences } from '@app/hooks/useTablePreferences';
import { Country } from '@app/model/Country';
import { useCountryStore } from "@app/store/entityStore";
import { hasRealmRole } from '@app/utils/keycloak';
import { ROLE } from '@app/utils/roles';
import { ActionList, ActionListItem, AlertVariant, Bullseye, Button, Card, CardBody, EmptyState, EmptyStateActions, EmptyStateBody, EmptyStateFooter, Form, FormGroup, FormHelperText, FormSelect, FormSelectOption, Grid, GridItem, HelperText, HelperTextItem, Icon, PageSection, Pagination, PaginationVariant, SearchInput, Split, SplitItem, TextInput, Title, Toolbar, ToolbarContent, ToolbarItem, Tooltip } from '@patternfly/react-core';
import { PencilAltIcon, SearchIcon } from '@patternfly/react-icons';
import { InnerScrollContainer, OuterScrollContainer, Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import * as React from 'react';

/**
 * Countries are reference data, and this page is read only with exactly one exception: the default
 * timezone.
 *
 * There is no New and no Remove, because the service has neither - the ISO list is seeded and fixed.
 * Two of the columns are load bearing beyond display as well: an imported class's country is
 * resolved against `name`, `alpha2` and `alpha3`, so an edit to any of them would quietly stop
 * imports matching. The service exposes one narrow PUT, for the timezone alone, and this page offers
 * exactly that.
 *
 * The timezone is different in kind from the rest: for the 31 countries spanning more than one zone
 * there is no factual answer, so the service seeded the capital's zone as a business default and
 * where a country's training business actually sits is a decision somebody has to be able to make.
 * It decides what time of day a date-only value arriving for a class in that country is recorded at,
 * which is why setting it is admin only.
 */

const emptyForm: Partial<Country> = {};

/** The zone the service falls back to when a country names none. */
const NO_TIMEZONE_FALLBACK = 'UTC';

const Countries: React.FunctionComponent = () => {
  const { addNotification } = useNotification();

  // --- Core State & Cache ---
  const { items: countries, error, retry } = useEntity(useCountryStore);
  const saveCountry = useCountryStore(state => state.save);

  // Writing the timezone needs the admin role - the endpoint is @RolesAllowed("admin"). Reading is
  // open to any authenticated user, so the page stays legible to everyone and editable by admins,
  // rather than being hidden from the people who may look but not touch. Only decides what to
  // offer; the service enforces it regardless.
  const isAdmin = hasRealmRole(ROLE.ADMIN);

  // --- Form State ---
  // Holds the country being edited and the timezone picked for it. Nothing else is writable, so
  // there is no "new" mode and no other field to buffer.
  const [formData, setFormData] = React.useState<Partial<Country>>(emptyForm);
  const [isEditing, setIsEditing] = React.useState(false);

  // --- UI State ---
  const [page, setPage] = React.useState(1);
  const { perPage, setPerPage } = useTablePreferences('countries', 10);
  const [filterText, setFilterText] = React.useState('');

  // --- Derived State: Filtering & Pagination ---
  const filteredRows = React.useMemo(() => {
    if (!filterText.trim()) return countries;
    const lowerFilter = filterText.toLowerCase();
    return countries.filter(c =>
      c.name.toLowerCase().includes(lowerFilter) ||
      c.continent.toLowerCase().includes(lowerFilter) ||
      c.alpha2.toLowerCase().includes(lowerFilter) ||
      c.timezone?.toLowerCase().includes(lowerFilter)
    );
  }, [countries, filterText]);

  const paginatedRows = React.useMemo(() => {
    const startIdx = (page - 1) * perPage;
    return filteredRows.slice(startIdx, startIdx + perPage);
  }, [filteredRows, page, perPage]);

  // --- Form Handlers ---
  const onEditClick = (country: Country) => {
    setFormData(country);
    setIsEditing(true);
  };

  const onCancelClick = () => {
    setFormData(emptyForm);
    setIsEditing(false);
  };

  /**
   * The three countries the tzdb associates no zone with - AN (dissolved), BV and HM (uninhabited).
   * The service refuses every value for them, so there is nothing to offer and nothing to save.
   */
  const hasNoZones = (country: Partial<Country>) => (country.timezones?.length ?? 0) === 0;

  const isUnchanged = formData.timezone === countries.find(c => c.id === formData.id)?.timezone;

  // --- Save -------------------------------------------------------------------------
  const onSaveClick = async () => {
    if (!formData.id || !formData.timezone) {
      addNotification('No timezone selected', AlertVariant.warning, 'Pick one of the country\'s timezones first.');
      return;
    }

    beginLoading();
    try {
      // Goes to PUT /country/{id}/timezone rather than a PUT of the whole country: CountryApi.update
      // routes it there, because the timezone is the only field the service will take.
      await saveCountry({ id: formData.id, timezone: formData.timezone }, false);

      addNotification(
        'Timezone updated',
        AlertVariant.success,
        `${formData.name} now defaults to ${formData.timezone}.`
      );
      onCancelClick();
    } catch (err: any) {
      addNotification('Error updating timezone', AlertVariant.danger, err.message);
    } finally {
      endLoading();
    }
  };

  // --- UI Helpers ---
  const buildPagination = (variant: 'bottom' | 'top') => (
    <Pagination
      isCompact={true}
      itemCount={filteredRows.length}
      page={page}
      perPage={perPage}
      onSetPage={(_evt, newPage) => setPage(newPage)}
      onPerPageSelect={(_evt, newPerPage, newPage) => { setPerPage(newPerPage); setPage(newPage); }}
      variant={variant as PaginationVariant}
      isStatic
    />
  );

  /** The stored zone, or what the service will actually use in its absence. */
  const renderTimezone = (country: Country) => (
    country.timezone
      ? country.timezone
      : (
        <Tooltip content={`No timezone is set, so the service reads dates for this country in ${NO_TIMEZONE_FALLBACK}.`}>
          <span className="tooltipped-text">{NO_TIMEZONE_FALLBACK} (not set)</span>
        </Tooltip>
      )
  );

  const editTooltip = !isAdmin
    ? 'Only an administrator can change a default timezone.'
    : 'Edit default timezone';

  return (
    <React.Fragment>
      {/* Header Section */}
      <PageSection hasBodyWrapper isWidthLimited>
        <Split>
          <SplitItem>
            <Title headingLevel="h1" size="4xl">Countries</Title>
          </SplitItem>
          <SplitItem isFilled></SplitItem>
          <SplitItem>
            <ActionList>
              <ActionListItem hidden={!isEditing}>
                <Tooltip content="Cancel edition">
                  <Button variant="secondary" onClick={onCancelClick}>
                    Cancel
                  </Button>
                </Tooltip>
              </ActionListItem>
              <ActionListItem hidden={!isEditing}>
                <Tooltip content="Save default timezone">
                  <Button variant="primary" onClick={onSaveClick} isDisabled={isUnchanged || hasNoZones(formData)}>
                    Save
                  </Button>
                </Tooltip>
              </ActionListItem>
              <ActionListItem>
                <PageActionsMenu onRefresh={retry} />
              </ActionListItem>
            </ActionList>
          </SplitItem>
        </Split>
      </PageSection>

      {/* Editor Form Section. Every field but the timezone is permanently disabled: this is ISO
          reference data, and the service has no endpoint that would take a change to it. */}
      <PageSection hasBodyWrapper isWidthLimited>
        <Card isPlain className='form-same-list-page'>
          <CardBody>
            <Form>
              <Grid hasGutter md={6}>
                <GridItem>
                  <FormGroup label="Name" fieldId="name">
                    <TextInput type="text" id="name" name="name" value={formData.name || ''} isDisabled readOnlyVariant="default" />
                  </FormGroup>
                </GridItem>
                <GridItem>
                  <FormGroup label="Full Name" fieldId="fullname">
                    <TextInput type="text" id="fullname" name="fullname" value={formData.fullname || ''} isDisabled readOnlyVariant="default" />
                  </FormGroup>
                </GridItem>
                <GridItem>
                  <FormGroup label="Alpha-2" fieldId="alpha2">
                    <TextInput type="text" id="alpha2" name="alpha2" value={formData.alpha2 || ''} isDisabled readOnlyVariant="default" />
                  </FormGroup>
                </GridItem>
                <GridItem>
                  <FormGroup label="Alpha-3" fieldId="alpha3">
                    <TextInput type="text" id="alpha3" name="alpha3" value={formData.alpha3 || ''} isDisabled readOnlyVariant="default" />
                  </FormGroup>
                </GridItem>
                <GridItem>
                  <FormGroup label="Continent" fieldId="continent">
                    <TextInput type="text" id="continent" name="continent" value={formData.continent || ''} isDisabled readOnlyVariant="default" />
                  </FormGroup>
                </GridItem>
                <GridItem>
                  <FormGroup label="Dial Code" fieldId="dialCode">
                    <TextInput type="text" id="dialCode" name="dialCode" value={formData.dialCode || ''} isDisabled readOnlyVariant="default" />
                  </FormGroup>
                </GridItem>
                <GridItem span={12}>
                  <FormGroup label="Default Timezone" fieldId="timezone">
                    {/* Only the country's own zones are offered, because those are the only ones the
                        service accepts: a zone that parses perfectly but belongs elsewhere is
                        refused, which is the mistake worth catching - it would otherwise put every
                        class in this country hours out. */}
                    <FormSelect
                      id="timezone"
                      name="timezone"
                      aria-label="Default timezone"
                      value={formData.timezone || ''}
                      isDisabled={!isEditing || hasNoZones(formData)}
                      onChange={(_e, value) => setFormData(prev => ({ ...prev, timezone: value }))}
                    >
                      <FormSelectOption value="" label={isEditing ? 'Select a timezone...' : ''} isPlaceholder />
                      {(formData.timezones ?? []).map(zone => (
                        <FormSelectOption key={zone} value={zone} label={zone} />
                      ))}
                    </FormSelect>
                    {isEditing && (
                      <FormHelperText>
                        <HelperText>
                          <HelperTextItem variant={hasNoZones(formData) ? 'warning' : 'default'}>
                            {hasNoZones(formData)
                              ? `The timezone database associates no zone at all with ${formData.name}, so none can be set.`
                              : `Decides what time of day a date-only value arriving for a class in ${formData.name} is recorded at. ${formData.timezones?.length} zone${formData.timezones?.length === 1 ? '' : 's'} to choose from.`}
                          </HelperTextItem>
                        </HelperText>
                      </FormHelperText>
                    )}
                  </FormGroup>
                </GridItem>
              </Grid>
            </Form>
          </CardBody>
        </Card>
      </PageSection>

      {/* List / Table Section */}
      <PageSection hasBodyWrapper isWidthLimited>
        <Card>
          <CardBody>
            {/* FADE OVERLAY WRAPPER */}
            <div style={{
              opacity: isEditing ? 0.4 : 1,
              pointerEvents: isEditing ? 'none' : 'auto',
              transition: 'opacity 0.3s ease-in-out'
            }}>
              <Toolbar id="compact-toolbar" clearAllFilters={() => setFilterText('')}>
                <ToolbarContent>
                  <ToolbarItem>
                    <SearchInput aria-label="Search countries" placeholder="Search by name, code or timezone..." value={filterText} onChange={(_e, val) => { setFilterText(val); setPage(1); }} onClear={() => setFilterText('')} />
                  </ToolbarItem>
                  <ToolbarItem variant="pagination">{buildPagination('top')}</ToolbarItem>
                </ToolbarContent>
              </Toolbar>

              {/* Frozen Actions column: the inner container is the horizontal scroll it sticks to, and
                  gridBreakPoint="" keeps the table tabular instead of stacking into cards. See app.css. */}
              <OuterScrollContainer style={{ maxHeight: 'calc(100vh - var(--cas-table-offset-lg))', minHeight: 0 }}>
                <InnerScrollContainer>
                  <Table aria-label="Countries Table" isStickyHeader variant='compact' gridBreakPoint="">
                    <Thead>
                      <Tr>
                        <Th width={10}>Flag</Th>
                        <Th>Name</Th>
                        <Th>Alpha-2</Th>
                        <Th>Alpha-3</Th>
                        <Th>Continent</Th>
                        <Th>Dial Code</Th>
                        <Th>Default Timezone</Th>
                        <Th width={10} isStickyColumn stickyMinWidth="90px" stickyRightOffset="0" hasLeftBorder>Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {paginatedRows.map((row) => (
                        <Tr key={row.id} isRowSelected={formData.id === row.id}>
                          <Td dataLabel="Flag"><span style={{ fontSize: '1.5rem' }}>{row.flag}</span></Td>
                          <Td dataLabel="Name"><b>{row.name}</b></Td>
                          <Td dataLabel="Alpha-2">{row.alpha2}</Td>
                          <Td dataLabel="Alpha-3">{row.alpha3}</Td>
                          <Td dataLabel="Continent">{row.continent}</Td>
                          <Td dataLabel="Dial Code">{row.dialCode}</Td>
                          <Td dataLabel="Default Timezone">{renderTimezone(row)}</Td>
                          <Td dataLabel="Actions" modifier="nowrap"
                            isStickyColumn stickyMinWidth="90px" stickyRightOffset="0" hasLeftBorder>
                            <ActionList>
                              <ActionListItem>
                                <Tooltip content={editTooltip}>
                                  <Button
                                    icon={<Icon size='lg'><PencilAltIcon /></Icon>}
                                    variant="plain"
                                    aria-label={`Edit the default timezone of ${row.name}`}
                                    onClick={() => onEditClick(row)}
                                    isDisabled={isEditing || !isAdmin}
                                  />
                                </Tooltip>
                              </ActionListItem>
                            </ActionList>
                          </Td>
                        </Tr>
                      ))}
                      {/* Nothing to show: distinguish "the load failed" from "no records match" */}
                      {error && filteredRows.length === 0 && (
                        <DataLoadErrorRow colSpan={8} error={error} entityLabel="countries" onRetry={retry} />
                      )}
                      {!error && filteredRows.length === 0 && (
                        <Tr>
                          <Td colSpan={8}>
                            <Bullseye>
                              <EmptyState headingLevel="h5" icon={SearchIcon} titleText="No results found">
                                <EmptyStateBody>No countries match your search criteria.</EmptyStateBody>
                                <EmptyStateFooter>
                                  <EmptyStateActions><Button variant="link" onClick={() => setFilterText('')}>Clear search</Button></EmptyStateActions>
                                </EmptyStateFooter>
                              </EmptyState>
                            </Bullseye>
                          </Td>
                        </Tr>
                      )}
                    </Tbody>
                  </Table>
                </InnerScrollContainer>
              </OuterScrollContainer>
              {buildPagination('bottom')}
            </div>
            {/* END FADE OVERLAY WRAPPER */}
          </CardBody>
        </Card>
      </PageSection>
    </React.Fragment>
  );
};

export { Countries };
